import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Trophy,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Upload,
  User,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  getEvent,
  listEventParticipants,
  listEventStandings,
  listEventPlayerResults,
  listPlayerConstructs,
} from "../../graphql/queries";
import {
  createEventStanding,
  updateEventStanding,
} from "../../graphql/mutations";
import { formatDate } from "../../utils/dateUtils";

const client = generateClient();

interface StandingEntry {
  id?: string;
  userId?: string;
  playerConstructId?: string;
  playerConstruct?: any;
  rank: number;
  points: number;
  omw: number;
  gw: number;
  ogw: number;
  isImported?: boolean; // Flag to identify imported data
}

const EventStandings: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [standings, setStandings] = useState<StandingEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch event details
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch current participants
  const {
    data: participants,
    isLoading: participantsLoading,
    error: participantsError,
  } = useQuery({
    queryKey: ["eventParticipants", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventParticipants,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventParticipants.items;
    },
    enabled: !!eventId,
  });

  // Fetch existing standings
  const {
    data: existingStandings,
    isLoading: standingsLoading,
    error: standingsError,
  } = useQuery({
    queryKey: ["eventStandings", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventStandings,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventStandings.items;
    },
    enabled: !!eventId,
  });

  // Fetch event player results (from EventLink imports)
  const {
    data: eventPlayerResults,
    isLoading: eventPlayerResultsLoading,
    error: eventPlayerResultsError,
  } = useQuery({
    queryKey: ["eventPlayerResults", eventId],
    queryFn: async () => {
      let allItems: any[] = [];
      let nextToken: string | null | undefined = undefined;

      // Fetch all pages of results
      do {
        const result: any = await client.graphql({
          query: listEventPlayerResults,
          variables: {
            filter: { eventId: { eq: eventId } },
            limit: 100,
            nextToken: nextToken,
          },
        });

        const items = result.data.listEventPlayerResults.items;
        allItems = [...allItems, ...items];
        nextToken = result.data.listEventPlayerResults.nextToken;

        console.log(`🔍 GraphQL Query Page:`, {
          itemsInPage: items.length,
          totalItemsSoFar: allItems.length,
          hasNextToken: !!nextToken,
        });
      } while (nextToken);

      console.log(`✅ Total EventPlayerResults fetched: ${allItems.length}`);
      return allItems;
    },
    enabled: !!eventId,
  });

  // Fetch player constructs
  const {
    data: playerConstructs,
    isLoading: playerConstructsLoading,
    error: playerConstructsError,
  } = useQuery({
    queryKey: ["playerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  // Initialize standings from participants when data loads
  React.useEffect(() => {
    // Debug logging
    console.log("EventStandings data loaded:", {
      eventPlayerResults: eventPlayerResults?.length || 0,
      playerConstructs: playerConstructs?.length || 0,
      existingStandings: existingStandings?.length || 0,
      participants: participants?.length || 0,
    });

    if (eventPlayerResults?.length) {
      console.log(
        "EventPlayerResults:",
        eventPlayerResults.map((r) => ({
          id: r.id,
          playerConstructId: r.playerConstructId,
          rank: r.rank,
          points: r.points,
        }))
      );

      // Show the actual player names from the EventPlayerResults
      const playerResultsWithNames = eventPlayerResults.map((r) => {
        const construct = playerConstructs?.find(
          (pc) => pc.id === r.playerConstructId
        );
        return {
          id: r.id,
          playerConstructId: r.playerConstructId,
          playerName: construct?.name || "UNKNOWN",
          rank: r.rank,
          points: r.points,
        };
      });
      console.log(
        "EventPlayerResults with player names:",
        playerResultsWithNames
      );

      // Check for any missing ranks
      const ranks = playerResultsWithNames
        .map((p) => p.rank)
        .sort((a, b) => a - b);
      const expectedRanks = Array.from(
        { length: ranks[ranks.length - 1] },
        (_, i) => i + 1
      );
      const missingRanks = expectedRanks.filter((r) => !ranks.includes(r));
      if (missingRanks.length > 0) {
        console.warn(`⚠️  Missing ranks in EventPlayerResults:`, missingRanks);
        console.warn(`   Available ranks:`, ranks);
      }

      // Check for UNKNOWN players (missing constructs)
      const unknownPlayers = playerResultsWithNames.filter(
        (p) => p.playerName === "UNKNOWN"
      );
      if (unknownPlayers.length > 0) {
        console.error(
          `❌ Players with missing PlayerConstructs:`,
          unknownPlayers
        );
      }
    }

    if (playerConstructs?.length) {
      console.log(
        "PlayerConstructs:",
        playerConstructs.map((pc) => ({
          id: pc.id,
          name: pc.name,
        }))
      );
    }

    const allStandings: StandingEntry[] = [];

    // Add traditional EventStanding records
    if (existingStandings?.length) {
      const traditionalStandings: StandingEntry[] = existingStandings.map(
        (standing: any) => ({
          id: standing.id,
          userId: standing.userId,
          rank: standing.rank,
          points: standing.points,
          omw: standing.omw || 0,
          gw: standing.gw || 0,
          ogw: standing.ogw || 0,
          isImported: false,
        })
      );
      allStandings.push(...traditionalStandings);
    }

    // Add EventPlayerResult records (from EventLink imports)
    if (eventPlayerResults?.length && playerConstructs?.length) {
      const importedStandings: StandingEntry[] = eventPlayerResults.map(
        (result: any) => {
          const playerConstruct = playerConstructs.find(
            (pc: any) => pc.id === result.playerConstructId
          );

          // Debug logging for missing player constructs
          if (!playerConstruct) {
            console.warn(`Missing player construct for result:`, {
              resultId: result.id,
              playerConstructId: result.playerConstructId,
              availableConstructs: playerConstructs.map((pc) => ({
                id: pc.id,
                name: pc.name,
              })),
            });
          }

          return {
            id: result.id,
            playerConstructId: result.playerConstructId,
            playerConstruct: playerConstruct,
            rank: result.rank,
            points: result.points,
            omw: result.omw || 0,
            gw: result.gw || 0,
            ogw: result.ogw || 0,
            isImported: true,
          };
        }
      );
      allStandings.push(...importedStandings);
    }

    // If no standings exist, initialize from participants
    if (allStandings.length === 0 && participants?.length) {
      const initialStandings: StandingEntry[] = participants
        .filter((p: any) => p.status === "CHECKED_IN")
        .map((participant: any, index: number) => ({
          userId: participant.userId,
          rank: index + 1,
          points: 0,
          omw: 0,
          gw: 0,
          ogw: 0,
          isImported: false,
        }));
      allStandings.push(...initialStandings);
    }

    // Sort by rank and set standings
    allStandings.sort((a, b) => a.rank - b.rank);
    setStandings(allStandings);
  }, [participants, existingStandings, eventPlayerResults, playerConstructs]);

  // Save standings mutation
  const saveStandingsMutation = useMutation({
    mutationFn: async (standingsData: StandingEntry[]) => {
      // Only save non-imported standings (imported data is read-only)
      const nonImportedStandings = standingsData.filter(
        (standing) => !standing.isImported
      );

      const promises = nonImportedStandings.map((standing) => {
        // Only process standings that have a userId (skip player constructs)
        if (!standing.userId) {
          return Promise.resolve(); // Skip player constructs
        }

        if (standing.id) {
          // Update existing standing
          return client.graphql({
            query: updateEventStanding,
            variables: {
              input: {
                id: standing.id,
                eventId: eventId!,
                userId: standing.userId,
                rank: standing.rank,
                points: standing.points,
                omw: standing.omw,
                gw: standing.gw,
                ogw: standing.ogw,
              },
            },
          });
        } else {
          // Create new standing
          return client.graphql({
            query: createEventStanding,
            variables: {
              input: {
                eventId: eventId!,
                userId: standing.userId,
                rank: standing.rank,
                points: standing.points,
                omw: standing.omw,
                gw: standing.gw,
                ogw: standing.ogw,
              },
            },
          });
        }
      });

      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventStandings", eventId] });
      setIsSubmitting(false);
      alert("Standings saved successfully!");
    },
    onError: (error: any) => {
      console.error("Error saving standings:", error);
      setIsSubmitting(false);
      alert("Failed to save standings. Please try again.");
    },
  });

  const handleSaveStandings = () => {
    setIsSubmitting(true);
    saveStandingsMutation.mutate(standings);
  };

  const handleStandingChange = (
    index: number,
    field: keyof StandingEntry,
    value: any
  ) => {
    const newStandings = [...standings];
    newStandings[index] = { ...newStandings[index], [field]: value };
    setStandings(newStandings);
  };

  const moveStanding = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === standings.length - 1) return;

    const newStandings = [...standings];
    const newIndex = direction === "up" ? index - 1 : index + 1;

    // Swap standings
    [newStandings[index], newStandings[newIndex]] = [
      newStandings[newIndex],
      newStandings[index],
    ];

    // Update ranks
    newStandings.forEach((standing, idx) => {
      standing.rank = idx + 1;
    });

    setStandings(newStandings);
  };

  const addStanding = () => {
    const newStanding: StandingEntry = {
      userId: "",
      rank: standings.length + 1,
      points: 0,
      omw: 0,
      gw: 0,
      ogw: 0,
    };
    setStandings([...standings, newStanding]);
  };

  const removeStanding = (index: number) => {
    const newStandings = standings.filter((_, idx) => idx !== index);
    // Update ranks
    newStandings.forEach((standing, idx) => {
      standing.rank = idx + 1;
    });
    setStandings(newStandings);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return `${rank}.`;
    }
  };

  const getUserName = (standing: StandingEntry) => {
    if (standing.userId) {
      const participant = participants?.find(
        (p: any) => p.userId === standing.userId
      );
      return participant ? `User ${standing.userId.slice(-4)}` : "Unknown User";
    } else if (standing.playerConstruct) {
      return standing.playerConstruct.name;
    }
    return "Unknown Player";
  };

  if (
    eventLoading ||
    participantsLoading ||
    standingsLoading ||
    eventPlayerResultsLoading ||
    playerConstructsLoading
  ) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (
    eventError ||
    participantsError ||
    standingsError ||
    eventPlayerResultsError ||
    playerConstructsError ||
    !event
  ) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading event standings data
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/events/${eventId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Event
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Event Standings</h1>
          <p className="text-muted-foreground mt-2">
            Manage final standings for {event.name}
          </p>
        </div>
        <Button
          onClick={handleSaveStandings}
          disabled={isSubmitting}
          className="flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {isSubmitting ? "Saving..." : "Save Standings"}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Event Info */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Event Information</CardTitle>
              <CardDescription>Event details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Trophy className="h-4 w-4" />
                <span>{event.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span>Format: {event.format}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span>Date: {formatDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span>
                  Checked-in Participants:{" "}
                  {participants?.filter((p: any) => p.status === "CHECKED_IN")
                    .length || 0}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Standings Management */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Final Standings</CardTitle>
                  <CardDescription>
                    Manually sort and set final standings
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={addStanding} variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Player
                  </Button>
                  <Button
                    onClick={() =>
                      navigate(`/admin/events/${eventId}/standings/import`)
                    }
                    variant="outline"
                    size="sm"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import from EventLink
                  </Button>
                  <Button
                    onClick={() =>
                      navigate(`/admin/events/${eventId}/standings/assignment`)
                    }
                    variant="outline"
                    size="sm"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Assign Users
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {standings.length > 0 ? (
                <div className="space-y-4">
                  {standings.map((standing, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 border rounded-lg"
                    >
                      {/* Rank */}
                      <div className="flex items-center gap-2 min-w-[60px]">
                        <span className="text-lg font-bold">
                          {getRankIcon(standing.rank)}
                        </span>
                        <div className="flex flex-col">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveStanding(index, "up")}
                            disabled={index === 0 || standing.isImported}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveStanding(index, "down")}
                            disabled={
                              index === standings.length - 1 ||
                              standing.isImported
                            }
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Player Selection */}
                      <div className="flex-1">
                        <Label htmlFor={`player-${index}`}>
                          Player{" "}
                          {standing.isImported && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              (Imported)
                            </span>
                          )}
                        </Label>
                        {standing.isImported ? (
                          <div className="flex items-center gap-2 p-2 border rounded-md bg-blue-50 dark:bg-blue-950">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {getUserName(standing)}
                            </span>
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              EventLink Import
                            </span>
                          </div>
                        ) : (
                          <Select
                            value={standing.userId || ""}
                            onValueChange={(value) =>
                              handleStandingChange(index, "userId", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select player" />
                            </SelectTrigger>
                            <SelectContent>
                              {participants
                                ?.filter((p: any) => p.status === "CHECKED_IN")
                                .map((participant: any) => (
                                  <SelectItem
                                    key={participant.userId}
                                    value={participant.userId}
                                  >
                                    {getUserName({
                                      userId: participant.userId,
                                    } as StandingEntry)}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {/* Points */}
                      <div className="w-24">
                        <Label htmlFor={`points-${index}`}>Points</Label>
                        <Input
                          id={`points-${index}`}
                          type="number"
                          value={standing.points}
                          onChange={(e) =>
                            handleStandingChange(
                              index,
                              "points",
                              parseInt(e.target.value) || 0
                            )
                          }
                          min="0"
                          disabled={standing.isImported}
                          className={
                            standing.isImported
                              ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              : ""
                          }
                        />
                      </div>

                      {/* OMW */}
                      <div className="w-20">
                        <Label htmlFor={`omw-${index}`}>OMW%</Label>
                        <Input
                          id={`omw-${index}`}
                          type="number"
                          value={standing.omw}
                          onChange={(e) =>
                            handleStandingChange(
                              index,
                              "omw",
                              parseInt(e.target.value) || 0
                            )
                          }
                          min="0"
                          max="100"
                          disabled={standing.isImported}
                          className={
                            standing.isImported
                              ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              : ""
                          }
                        />
                      </div>

                      {/* GW */}
                      <div className="w-20">
                        <Label htmlFor={`gw-${index}`}>GW%</Label>
                        <Input
                          id={`gw-${index}`}
                          type="number"
                          value={standing.gw}
                          onChange={(e) =>
                            handleStandingChange(
                              index,
                              "gw",
                              parseInt(e.target.value) || 0
                            )
                          }
                          min="0"
                          max="100"
                          disabled={standing.isImported}
                          className={
                            standing.isImported
                              ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              : ""
                          }
                        />
                      </div>

                      {/* OGW */}
                      <div className="w-20">
                        <Label htmlFor={`ogw-${index}`}>OGW%</Label>
                        <Input
                          id={`ogw-${index}`}
                          type="number"
                          value={standing.ogw}
                          onChange={(e) =>
                            handleStandingChange(
                              index,
                              "ogw",
                              parseInt(e.target.value) || 0
                            )
                          }
                          min="0"
                          max="100"
                          disabled={standing.isImported}
                          className={
                            standing.isImported
                              ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              : ""
                          }
                        />
                      </div>

                      {/* Remove */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStanding(index)}
                        disabled={standing.isImported}
                        className="text-red-500 hover:text-red-700 disabled:text-gray-400 dark:disabled:text-gray-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Standings</h3>
                  <p className="text-muted-foreground mb-4">
                    Add players to create final standings.
                  </p>
                  <Button onClick={addStanding}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Player
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventStandings;
